import type {
  SupportChatConversation,
  SupportChatMessage,
  SupportChatSender,
} from '@/types';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const normalizeId = (raw: unknown): string | number | null => {
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  return null;
};

const normalizeSender = (raw: unknown): SupportChatSender => {
  const s = typeof raw === 'string' ? raw.toLowerCase() : '';
  if (s === 'user' || s === 'customer' || s === 'business') return 'user';
  if (s === 'agent' || s === 'support' || s === 'staff') return 'agent';
  if (s === 'assistant' || s === 'ai' || s === 'bot') return 'bot';
  return 'bot';
};

export const normalizeSupportChatMessage = (
  raw: unknown,
  index = 0
): SupportChatMessage | null => {
  if (!isRecord(raw)) return null;

  const content =
    typeof raw.content === 'string'
      ? raw.content
      : typeof raw.message === 'string'
        ? raw.message
        : typeof raw.text === 'string'
          ? raw.text
          : '';

  if (!content.trim()) return null;

  const id =
    normalizeId(raw.id) ??
    normalizeId(raw.messageId) ??
    normalizeId(raw.uuid) ??
    `msg-${index}-${content.slice(0, 12)}`;

  const createdAt =
    typeof raw.createdAt === 'string'
      ? raw.createdAt
      : typeof raw.created_at === 'string'
        ? raw.created_at
        : typeof raw.created_time === 'string'
          ? raw.created_time
          : new Date().toISOString();

  const correlationId =
    typeof raw.correlationId === 'string' ? raw.correlationId : undefined;

  return {
    id,
    content: content.trim(),
    sender: normalizeSender(raw.sender ?? raw.role ?? raw.from),
    createdAt,
    correlationId,
  };
};

export const normalizeSupportChatConversation = (
  raw: unknown
): SupportChatConversation | null => {
  if (!isRecord(raw)) return null;

  const id =
    normalizeId(raw.id) ??
    normalizeId(raw.conversationId) ??
    normalizeId(raw.uuid);
  if (id === null) return null;

  const messagesRaw = Array.isArray(raw.messages)
    ? raw.messages
    : Array.isArray(raw.data)
      ? raw.data
      : [];

  const messages = messagesRaw
    .map((m, i) => normalizeSupportChatMessage(m, i))
    .filter((m): m is SupportChatMessage => m != null);

  return {
    id,
    messages,
    unreadCount:
      typeof raw.unreadCount === 'number'
        ? raw.unreadCount
        : typeof raw.unread_count === 'number'
          ? raw.unread_count
          : undefined,
  };
};

export const createLocalWelcomeMessage = (
  content: string
): SupportChatMessage => ({
  id: `welcome-${Date.now()}`,
  content,
  sender: 'bot',
  createdAt: new Date().toISOString(),
});
